import { useEffect, useRef, useState } from 'react';
import { getGsap, hasFinePointer, prefersReducedMotion } from '@/lib/motion';

// MagneticCursor — a soft ring that trails the mouse and a small dot that
// sits on it. The system cursor stays visible (a hidden cursor is a usability
// trap the moment a frame drops); this only adds the premium trail.
//
// Anything interactive (links, buttons, [data-cursor]) makes the ring swell
// and tint; an element with data-cursor="<label>" shows that word inside the
// ring (e.g. data-cursor="Play"). Mouse/trackpad only: phones, tablets,
// reduced-motion visitors and tests render nothing.

const INTERACTIVE = 'a, button, [role="button"], [data-cursor], input, select, textarea, label';

export function MagneticCursor() {
  const [enabled, setEnabled] = useState(false);
  const ringRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const [label, setLabel] = useState('');

  useEffect(() => {
    setEnabled(hasFinePointer() && !prefersReducedMotion());
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const ring = ringRef.current;
    const dot = dotRef.current;
    if (!ring || !dot) return undefined;
    const { gsap } = getGsap();
    gsap.set([ring, dot], { xPercent: -50, yPercent: -50, opacity: 0 });
    const ringX = gsap.quickTo(ring, 'x', { duration: 0.45, ease: 'power3.out' });
    const ringY = gsap.quickTo(ring, 'y', { duration: 0.45, ease: 'power3.out' });
    const dotX = gsap.quickTo(dot, 'x', { duration: 0.08, ease: 'power3.out' });
    const dotY = gsap.quickTo(dot, 'y', { duration: 0.08, ease: 'power3.out' });
    let hovering: Element | null = null;
    let shown = false;

    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      if (!shown) {
        shown = true;
        gsap.to([ring, dot], { opacity: 1, duration: 0.3 });
      }
      ringX(e.clientX); ringY(e.clientY);
      dotX(e.clientX); dotY(e.clientY);
      const target = (e.target as Element | null)?.closest?.(INTERACTIVE) ?? null;
      if (target !== hovering) {
        hovering = target;
        const text = target?.getAttribute('data-cursor') || '';
        setLabel(text);
        gsap.to(ring, {
          scale: target ? (text ? 2.6 : 1.8) : 1,
          backgroundColor: target ? 'rgba(244,190,82,0.12)' : 'rgba(244,190,82,0)',
          borderColor: target ? 'rgba(244,190,82,0.65)' : 'rgba(255,255,255,0.35)',
          duration: 0.35,
          ease: 'power3.out',
        });
        gsap.to(dot, { scale: target ? 0 : 1, duration: 0.2 });
      }
    };
    const onDown = () => gsap.to(ring, { scale: '*=0.85', duration: 0.12, yoyo: true, repeat: 1 });
    const onLeave = () => { shown = false; gsap.to([ring, dot], { opacity: 0, duration: 0.25 }); };

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', onDown, { passive: true });
    document.documentElement.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown);
      document.documentElement.removeEventListener('pointerleave', onLeave);
      gsap.killTweensOf([ring, dot]);
    };
  }, [enabled]);

  if (!enabled) return null;
  return (
    <>
      <div
        ref={ringRef}
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 z-[9999] flex h-9 w-9 items-center justify-center rounded-full border border-white/35 mix-blend-normal"
      >
        {label && (
          <span className="select-none text-[5px] font-bold uppercase tracking-[0.2em] text-white">{label}</span>
        )}
      </div>
      <div
        ref={dotRef}
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 z-[9999] h-1.5 w-1.5 rounded-full bg-[#F4BE52]"
      />
    </>
  );
}
